// Enhanced Job Status Types
export type JobStatus = 
  | 'Pending'                    // Scheduled, waiting for start time
  | 'Open'                       // Ready to work, can be started
  | 'Active'                     // Currently being worked on (timer running)
  | 'Awaiting Client Response'   // Blocked, waiting for client email
  | 'Overdue'                    // Past due date
  | 'Closed'                     // Completed work
  | 'Cancelled'                  // Terminated
  | 'Archived';                  // Historical

// Valid status transitions - Maximum flexibility for real-world workflows
export const STATUS_TRANSITIONS: Record<JobStatus, JobStatus[]> = {
  'Pending': ['Open', 'Active', 'Awaiting Client Response', 'Overdue', 'Closed', 'Cancelled', 'Archived'],
  'Open': ['Active', 'Pending', 'Awaiting Client Response', 'Overdue', 'Closed', 'Cancelled', 'Archived'],
  'Active': ['Open', 'Pending', 'Awaiting Client Response', 'Overdue', 'Closed', 'Cancelled', 'Archived'],
  'Awaiting Client Response': ['Open', 'Pending', 'Active', 'Overdue', 'Closed', 'Cancelled', 'Archived'],
  'Overdue': ['Active', 'Open', 'Pending', 'Awaiting Client Response', 'Closed', 'Cancelled', 'Archived'],
  'Closed': ['Open', 'Pending', 'Active', 'Awaiting Client Response', 'Overdue', 'Cancelled', 'Archived'],
  'Cancelled': ['Open', 'Pending', 'Active', 'Awaiting Client Response', 'Overdue', 'Closed', 'Archived'],
  'Archived': ['Open', 'Pending', 'Active', 'Awaiting Client Response', 'Overdue', 'Closed', 'Cancelled'] // Can be unarchived
};

// Status change tracking
export interface StatusChange {
  timestamp: string;
  fromStatus: JobStatus;
  toStatus: JobStatus;
  changedBy: 'user' | 'system' | 'email-trigger' | 'activity-timeout';
  reason?: string;
  userId?: string;
}

// Scheduled transition for automated status changes
export interface ScheduledTransition {
  id: string;
  fromStatus: JobStatus;
  toStatus: JobStatus;
  triggerTime: string;
  triggerType: 'scheduled_start' | 'due_date' | 'activity_timeout' | 'email_received';
  enabled: boolean;
  createdAt: string;
}

// Active work session tracking
export interface ActiveSession {
  startTime: string;
  userId: string;
  deviceId?: string;
  lastHeartbeat: string;
  totalMinutesActive: number;
  isPaused?: boolean; // Track if the session is paused
  pausedAt?: string; // When the session was paused
  pausedDuration?: number; // Total time spent paused (in minutes)
  activityCheck?: {
    promptedAt: string;
    expiresAt: string;
    responded: boolean;
  };
}

// Work session record - tracks individual work periods
export interface WorkSession {
  id: string; // Unique identifier for the session
  startTime: string; // When the session started
  endTime: string; // When the session ended
  durationMinutes: number; // Total minutes worked in this session
  userId: string; // Who worked on this session
  deviceId?: string; // Device identifier
  endReason: 'completed' | 'paused' | 'status_changed' | 'timeout'; // Why the session ended
  notes?: string; // Optional notes about the work done
  pausedDuration?: number; // Time spent paused within this session (in minutes)
  statusAtStart: JobStatus; // Job status when session started
  statusAtEnd: JobStatus; // Job status when session ended
}

// Job automation configuration
export interface JobAutomation {
  enabled: boolean;
  lastStatusChange: StatusChange;
  scheduledTransitions: ScheduledTransition[];
  blockAutoTransitions: boolean; // Used for "Awaiting Client Response" state
}

// Job timing configuration
export interface JobTiming {
  scheduledStartTime?: string;
  estimatedDurationMinutes?: number;
  activityCheckIntervalMinutes: number; // default 30
  activityTimeoutMinutes: number; // default 5
}

export interface Job {
  id: string; // Unique identifier for the job
  jobLabel: string; // A label or title for the job
  description: string; // Detailed description of the job
  customerId: string[]; // Reference to the associated customer's ID
  // Legacy timer fields — kept optional for backward compat with old job
  // documents. New code paths do not set or read these.
  unbilledHours?: number;
  totalHours?: number;
  creationDate: string; // Date when the job was created
  dueDate: string; // Due date for the job
  status: JobStatus; // Enhanced status with new types
  lastEdited: string; // Date when the job was last edited
  
  // Optional automation fields for backward compatibility
  automation?: JobAutomation;
  
  // Active session tracking (null when not active)
  activeSession?: ActiveSession | null;
  
  // Timing configuration
  timing?: JobTiming;
  
  // Status history for audit trail
  statusHistory?: StatusChange[];
  
  // Work sessions - historical record of all work periods
  workSessions?: WorkSession[];
  
  // Legacy field - will be deprecated in favor of workSessions
  accumulatedTimeMinutes?: number;

  // Work Hub: denormalized communication fields (optional; may be absent on older jobs)
  recentEmailIds?: string[];
  lastCommunicationAt?: any;

  // Dashboard "inbound unread" dismissal — clicking the [×] next to the
  // [N] badge on a job card sets this to now(). The count filter ignores
  // inbound emails older than this timestamp, so dismissing clears the
  // badge until a new message arrives after this time.
  attentionDismissedAt?: string;   // ISO timestamp

  // Job Workspace: associated chat sessions for LLM collaboration
  chatSessionIds?: string[];
}

// Helper function to create default automation settings
export const createDefaultJobAutomation = (): JobAutomation => ({
  enabled: true,
  lastStatusChange: {
    timestamp: new Date().toISOString(),
    fromStatus: 'Open',
    toStatus: 'Open',
    changedBy: 'system',
    reason: 'Initial job creation'
  },
  scheduledTransitions: [],
  blockAutoTransitions: false
});

// Helper function to create default timing settings
export const createDefaultJobTiming = (): JobTiming => ({
  activityCheckIntervalMinutes: 30,
  activityTimeoutMinutes: 5
});

// Helper function to create a complete Job with all required fields
export const createJobWithDefaults = (partialJob: Partial<Job>): Job => ({
  id: '',
  jobLabel: '',
  description: '',
  customerId: [],
  unbilledHours: 0,
  totalHours: 0,
  creationDate: new Date().toISOString().split('T')[0], // Format as YYYY-MM-DD for date inputs
  dueDate: '',
  status: 'Open',
  lastEdited: new Date().toISOString(),
  automation: createDefaultJobAutomation(),
  activeSession: null,
  timing: createDefaultJobTiming(),
  statusHistory: [],
  ...partialJob
});

// Helper function to ensure a job has all required fields (for migrating existing jobs)
export const ensureJobDefaults = (job: any): Job => ({
  ...job,
  status: job.status || 'Open',
  automation: job.automation || createDefaultJobAutomation(),
  activeSession: job.activeSession || null,
  timing: job.timing || createDefaultJobTiming(),
  statusHistory: job.statusHistory || []
});

export interface NewJobModalProps {
  show: boolean;
  onClose: () => void;
  // Optional initial values for pre-populating the form (e.g., when creating from email)
  initialCustomerId?: string;
  initialDescription?: string;
  // Optional callback when job is successfully created (returns the new job with ID)
  onJobCreated?: (job: Job) => void;
}

export interface FeedItem {
  content: string;
  timestamp: any; // Can be Firestore Timestamp or number
  filePath?: string;
  id: string;
  info?: string;
  fileUrl?: string;
  type?: string; // 'update', 'file', etc.
  source?: 'human' | 'llm' | 'system'; // Who created the update
  attachment?: {
    name: string;
    size: number;
    type: string;
  };
}

// Alias for backward compatibility
export type FeedEntry = FeedItem;

export interface Task {
  jobId: string;
  jobLabel: string;
  task: string;
  lastEdited: string;
  id: string;
  title: string;
  description: string;
  feed: FeedItem[];
  timestamp?: number;
  info?: string;
  status?: 'pending' | 'in_progress' | 'completed' | 'done' | 'cancelled' | 'deleted';
  priority?: 'low' | 'medium' | 'high';
  dueDate?: string;
  createdAt?: string;
  assignedTo?: string;
  deletedAt?: string;
}

export interface CalendarEvent {
  id: string;
  jobId?: string;
  title: string;
  client: string[];
  start: string;
  end: string;
  allDay?: boolean;
  description?: string;
  location?: string;
  attendees?: string[];
  recurrence?: string[];
  reminders?: { method: string; minutes: number }[];
  colour?: string;

  // All optional so the documents already in Firestore stay valid.
  /**
   * Last day a recurring event may occur, 'YYYY-MM-DD', INCLUSIVE.
   * undefined means "never ends" — which is what every legacy document says,
   * so readers must not treat a missing value as "ended".
   */
  recurrenceEnd?: string;
  createdBy?: string;               // auth uid of whoever created the event
  createdAt?: string;               // ISO
  updatedAt?: string;               // ISO
  /**
   * Soft delete. Absent means active: legacy documents have no status field,
   * so readers must test `status !== 'cancelled'`, never `status === 'active'`.
   */
  status?: 'active' | 'cancelled';
  /**
   * Days ('YYYY-MM-DD') on which this recurring series does NOT occur, written
   * when the user deletes a single occurrence rather than the whole series.
   * The base document keeps its own dates, so removing a date from here
   * restores that occurrence.
   */
  exceptions?: string[];
}

export interface Email {
  id: string;
  date: Date;
  from: {
    value: {
      address: string;
      name: string;
    }[];
    html: string;
    text: string;
  };
  subject: string;
  text: string;
  hasAttachments?: boolean;
  formattedDate: string;
}